<template>
  <article
    :class="rootClasses"
    data-testid="workflow-step"
    :data-current="isCurrent ? 'true' : 'false'"
  >
    <div class="flex items-start justify-between gap-2">
      <span :class="markerClasses">
        {{ index }}
      </span>
      <span v-if="isPast" class="text-accent-strong" aria-hidden="true">
        <BaseIcon name="check" :size="18" />
      </span>
    </div>
    <h3 class="text-base font-bold text-ink-strong md:text-lg">{{ title }}</h3>
    <p class="text-sm text-ink-muted">{{ subtitle }}</p>
    <div class="mt-auto">
      <p class="text-xs font-semibold tracking-wide text-ink-strong/80 uppercase">
        {{ receivesLabel }}
      </p>
      <ul class="mt-2 flex flex-col gap-1.5 text-sm text-ink">
        <li v-for="item in receives" :key="item" class="flex items-start gap-2">
          <BaseIcon name="check" :size="14" class="mt-1 shrink-0 text-accent" />
          <span>{{ item }}</span>
        </li>
      </ul>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva } from 'class-variance-authority';
import BaseIcon from '@/components/base/BaseIcon.vue';

const rootVariants = cva(
  'relative flex h-full flex-col gap-4 rounded-2xl border p-5 md:p-6 hover:shadow-md hover:shadow-accent/50',
  {
    variants: {
      isCurrent: {
        true: 'border-accent bg-accent-soft text-ink-strong shadow-[var(--shadow-brand)]',
        false: 'border-stroke bg-paper text-ink',
      },
    },
    defaultVariants: { isCurrent: false },
  },
);

const markerVariants = cva(
  'inline-flex h-10 w-10 items-center justify-center rounded-xl font-extrabold tabular-nums',
  {
    variants: {
      isCurrent: {
        true: 'bg-accent text-ink-inverse',
        false: 'bg-accent-soft text-accent-strong',
      },
    },
    defaultVariants: { isCurrent: false },
  },
);

interface Props {
  index: string;
  title: string;
  subtitle: string;
  receives: readonly string[];
  receivesLabel: string;
  isCurrent?: boolean;
  isPast?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isCurrent: false,
  isPast: false,
});

const rootClasses = computed(() => rootVariants({ isCurrent: props.isCurrent }));
const markerClasses = computed(() => markerVariants({ isCurrent: props.isCurrent }));
</script>
